import { Box, CardProps, Flex } from '@chakra-ui/react';
import { FaMoneyCheckDollar } from 'react-icons/fa6';

import { LDFlags } from '../../../../constants/flags';
import {
  PayoffRequest,
  PayoffRequestStatus,
  useExternalUserPayoffRequestQuery,
} from '../../../../gql/generated/graphql';
import { useFlag } from '../../../../hooks';
import { DealStateEnum } from '../../../../utils/types/deal';
import { DealWithFarthestState, SHOW_PAYOFF_PANEL_STATES } from '../../utils';
import DashboardCard from '../DashboardCard';
import EducationalContentPayoff from './EducationalContentPayoff';
import ExtendedProcessingPayoff from './ExtendedProcessingPayoff';
import InProgressPayoff from './InProgressPayoff';
import SuccessPayoff from './SuccessPayoff';

type Props = {
  deal?: DealWithFarthestState;
  _cardProps?: CardProps;
};

const DashboardPayoffCard = ({ deal, _cardProps }: Props) => {
  const payoffIntelligenceEnabled = useFlag(LDFlags.PAYOFF_INTELLIGENCE_ENABLED);
  const payoffIntelligencePanelEnabled = useFlag(LDFlags.COM_PAYOFF_INTELLIGENCE_DASHBOARD_PANEL);
  const { data: payoffRequestData, loading: payoffRequestLoading } =
    useExternalUserPayoffRequestQuery({
      variables: {
        dealId: deal?.id,
      },
      skip: !deal?.id || !payoffIntelligencePanelEnabled || !payoffIntelligenceEnabled,
    });

  const payoffRequest = payoffRequestData?.externalUserPayoffRequest as PayoffRequest;

  const showPanel =
    payoffRequest &&
    !payoffRequestLoading &&
    payoffIntelligenceEnabled &&
    payoffIntelligencePanelEnabled &&
    SHOW_PAYOFF_PANEL_STATES.includes(deal?.state as DealStateEnum);

  if (!showPanel) {
    return null;
  }

  const payoffRequestStatus = payoffRequest.status as PayoffRequestStatus;

  const sucessStatuses = [PayoffRequestStatus.Completed].includes(payoffRequestStatus);

  const inProgressStatuses = [PayoffRequestStatus.Fetching, PayoffRequestStatus.Pending].includes(
    payoffRequestStatus,
  );

  const extendedProcessingStatuses = [
    PayoffRequestStatus.Failed,
    PayoffRequestStatus.FailureRequested,
    PayoffRequestStatus.Cancelled,
  ].includes(payoffRequestStatus);

  return (
    <DashboardCard title="Payoff" icon={FaMoneyCheckDollar} {..._cardProps}>
      <Flex flexDir={{ base: 'column', md: 'row' }} gap="40px">
        <Box w={{ base: 'full', md: 'calc(50% - 20px)' }}>
          {sucessStatuses && <SuccessPayoff payoffRequest={payoffRequest} />}
          {inProgressStatuses && <InProgressPayoff />}
          {extendedProcessingStatuses && <ExtendedProcessingPayoff />}
        </Box>
        <Box w="1px" bg="gray.200" opacity={0.6} display={{ base: 'none', md: 'block' }} />
        <EducationalContentPayoff />
      </Flex>
    </DashboardCard>
  );
};

export default DashboardPayoffCard;
